import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy } from '@angular/core';
import { PrepareActions } from '@app/client/uploader/store/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { Background } from '@app/shared/models/background';
import { Customization } from '@app/shared/models/customization';
import { Logo } from '@app/shared/models/logo';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalCustomizationForbiddenComponent } from '../modal-customization-forbidden/modal-customization-forbidden.component';
import { ModalCustomizationReceiverAddBackgroundComponent } from '../modal-customization-receiver-add-background/modal-customization-receiver-add-background.component';

type Section = 'backgrounds' | 'logos';
@Component({
  selector: 'app-modal-customization-receiver',
  templateUrl: './modal-customization-receiver.component.html',
  styleUrls: ['./modal-customization-receiver.component.scss'],
  animations: [
    trigger('animateHeader', [
      state('in', style({ transform: 'translateX({{startPosition}})' }), { params: { startPosition: '0px' } }),
      state('out', style({ transform: 'translateX({{endPosition}})' }), { params: { endPosition: '-300px' } }),
      transition('in => out', animate('0.4s ease-in-out')),
      transition('out => in', animate('0.4s linear'))
    ]),
    trigger('loader', [
      state('in', style({ opacity: '{{startOpacity}}' }), { params: { startOpacity: '0.9' } }),
      state('out', style({ opacity: '{{endOpacity}}' }), { params: { endOpacity: '0' } }),
      transition('in => out', animate('0.4s ease-in-out')),
      transition('out => in', animate('0.4s linear'))
    ]),
  ],
})
export class ModalCustomizationReceiverComponent implements OnDestroy {

  account$: Observable<any>;
  isFreemium$: Observable<any>;
  domain$: Observable<GetRecordOutput['record']>;

  customizationEdited$: Observable<Partial<Customization>>;
  backgrounds$: Observable<Background[]>;
  logos$: Observable<Logo[]>;
  customizationMediaLoading$: Observable<boolean>;

  customizationEdited: Partial<Customization>;
  customizationLoading: boolean;
  backgrounds: Background[];
  logos: Logo[];
  selectedLogo: Logo;
  selectedBackground: Background;
  modalBackgroundOpened = false;
  freemiumModalOpened = false;

  selectedSection: Section = 'backgrounds';

  modalLogoOpened = false;
  backgroundIsLoading = false;
  isCustomizationEdited = false;
  previewCustomizationReady = true;

  account: any;
  isFreemium: boolean;

  constructor(
    private readonly store$: Store<fromRoot.State & fromUploader.State>,
    public ref: DialogRef,
    private dialog: DialogService,
    private gtmService: GoogleTagManagerService,
  ) {
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);

    this.account$ = this.store$.select(fromRoot.getActiveIdentity).pipe(take(1));
    this.account$.subscribe(account => this.account = account);

    this.isFreemium$ = this.store$.select(fromRoot.getIsFreemium).pipe(take(1));
    this.isFreemium$.subscribe(isFreemium => this.isFreemium = isFreemium);

    this.customizationEdited$ = this.store$.select(fromUploader.getCustomizationEdited);
    this.logos$ = this.store$.select(fromUploader.getCustomizationLogos);
    this.backgrounds$ = this.store$.select(fromUploader.getCustomizationBackgrounds);
    this.customizationMediaLoading$ = this.store$.select(fromUploader.getCustomizationMediaLoading);

    this.customizationEdited$.subscribe(customizationEdited => this.customizationEdited = Object.assign({}, customizationEdited));
    this.store$.select(fromUploader.getCustomizationLoading).subscribe(customizationLoading => this.customizationLoading = customizationLoading);
    this.logos$.subscribe(logos => this.logos = logos);
    this.backgrounds$.subscribe(backgrounds => this.backgrounds = backgrounds);
  }

  /* Close modal */
  back() {
    this.store$.dispatch(PrepareActions.ResetCustomisationEdited());
    this.closeModal();
    this.selectedBackground = null;
    this.selectedLogo = null;
  }

  /* Select Background panel */
  onSwitchBackgroundSection(): void {
    this.selectedSection = 'backgrounds';
  }

  /* Select Logo panel */
  onSwitchLogoSection(): void {
    this.selectedSection = 'logos';
  }

  /* Action on select background */
  //FIX ME The set 
  onSelectBackground(background): void {
    this.previewCustomizationReady = false;
    setTimeout(() => {
      this.selectedBackground = background;
      this.customizationEdited.background = background;
      this.isCustomizationEdited = true;
      this.previewCustomizationReady = true;
    }, 0);
  }

  /* Action on select logo */
  onSelectLogo(logo): void {
    this.previewCustomizationReady = false;
    setTimeout(() => {
      this.selectedLogo = logo;
      this.customizationEdited.logo = logo;
      this.isCustomizationEdited = true;
      this.previewCustomizationReady = true;
    }, 0);
  }

  /* Action when click on add background */
  onAddBackground(): void {
    if (this.isFreemium) {
      this.lockFreemium();
    } else {
      this.openModalAddBackground();
      this.isCustomizationEdited = true;
    }
  }

  /* Action when click on add logo */
  onAddLogo(): void {
    if (this.isFreemium) {
      this.lockFreemium();
    } else {
      this.openModalAddBackground();
      this.isCustomizationEdited = true;
    }
  }

  /* FIX ME HANDLE THIS WITH LIBRARY */
  closeModalBackground(): void {
  }

  lockFreemium(): boolean {
    if (this.isFreemium) {
      this.dialog.open(ModalCustomizationForbiddenComponent, {
        closeButton: false,
        enableClose: true,
        size: 'sm',
      });
      this.gtmService.pushTag({
        event: Constant.eventGTM.CLICK_ADD_BACKGROUND_LOGO_FREEMIUM,
      });
      return false
    } else {
      return true;
    }
  }

  /* FIX ME HANDLE THIS WITH LIBRARY */
  closeFreemiumModal(): void {
    this.freemiumModalOpened = false;
  }

  /* Getter background selected */
  isBackgroundSelected(background: Background): boolean {
    return this.customizationEdited?.background?.id === background.id;
  }

  /* Getter logo selected */
  isLogoSelected(logo: Logo): boolean {
    return this.customizationEdited?.logo?.id === logo.id;
  }

  // FIX ME HANDLE LOADING IN COMPONENT
  createLogo(image: any): void {
    const _image = [image[0]];
    this.store$.dispatch(PrepareActions.addLogoToAttach({ image: _image }));
    this.isCustomizationEdited = true;
  }

  closeModal() {
    this.ref.close();
  }

  save(): void {
    this.store$.dispatch(PrepareActions.SetTransferCustomization({ logo: this.customizationEdited.logo, background: this.customizationEdited.background }));
    this.closeModal();
  }

  /* Open modal background */
  openModalAddBackground() {
    if (this.isFreemium) {
      this.dialog.open(ModalCustomizationForbiddenComponent, {
        closeButton: false,
        enableClose: true,
        size: 'sm',
      });
    } else {
      this.dialog.open(ModalCustomizationReceiverAddBackgroundComponent, {
        id: "addBackGround", // to make sure in production it works ;)
        closeButton: true,
        enableClose: true,
        size: 'sm',
      });
    }
  }

  ngOnDestroy(): void {
    // this.closeModal();
  }

  // FIX ME Helper ?
  resolveSrcUrlString(src: string): string {
    if (src?.startsWith('http')) {
      return `url(${src})`;
    } else {
      return `url(//${src})`;
    }
  }
}
